import { redirect } from 'redux-first-router';
import isEmpty from 'lodash/isEmpty';

import { COMPONENT_TEMPLATE, EDIT_STATUS_SAVED, SEARCH_TEMPLATE } from 'app/App/const';
import {
  getConfigSuccess,
  getConfigFailure,
  getConfigRequest,
  getAudienceTagsSuccess,
  getAudienceTagsFailure,
  getAudienceTagsRequest,
  switchApplicationMode
} from 'app/App/actions';
import contractMapSelector from 'app/App/selectors/contractMap';
import audienceTagsSelector from 'app/App/selectors/audienceTags';
import {
  getExistingComponent,
  getExistingComponentFailure,
  getExistingComponentSuccess
} from 'app/CanvasComponent/actions';
import { isFetchingComponent } from 'app/EditorPane/actions';
import { fetchExperiment } from 'app/Experiments/thunks';
import { isExperiment } from 'app/Experiments/utils';
import { notifyFailure } from 'app/Notifications/actions';
import { getPage } from 'app/Page/thunks';
import { setFilters } from 'app/Search/actions';
import { getSearchResults } from 'app/Search/thunks';
import { changeDir } from 'app/Tree/actions';
import { cwdSelector, treeSelector } from 'app/Tree/selectors';
import { getFolderThunk } from 'app/Tree/thunks';
import preferredHomeFolderIdSelector from 'app/UserInfo/selectors/preferredHomeFolderId';
import { getUserDataThunk } from 'app/UserInfo/thunks';
import { contractUtils } from 'lib/contract';
import { EN } from 'lib/locales';
import { isBlank } from 'lib/string';
import { PUBLISHED } from 'lib/versions';
import { getComponent } from 'utils/api/component';
import { createNewComponentObject } from 'utils/factory';
import { PATHNAME_NAUTH } from './const';
import { visitApp, visitContract } from './actions';
import payloadSelector from './selectors/payload';
import querySelector from './selectors/query';

const scope = ['openid', 'first_name', 'last_name', 'lan_id', 'groups_filter'].join(' ');
const {
  REACT_APP_NAUTH_URL_V1: nauthUrl,
  REACT_APP_NAUTH_CLIENT_ID: clientId,
  REACT_APP_NAUTH_CODE_CHALLENGE: codeChallenge,
  REACT_APP_NAUTH_STATE: nauthState
} = process.env;

const getAuthorizeParams = redirectUrl => {
  const params = {
    client_id: clientId,
    redirect_uri: redirectUrl,
    response_type: 'code',
    code_challenge_method: 'S256',
    state: nauthState,
    code_challenge: codeChallenge,
    scope
  };
  return params;
};

export const buildAuthorizeUrl = redirectUrl => {
  const authorizeParams = getAuthorizeParams(redirectUrl);
  const query = new URLSearchParams(authorizeParams);
  const authorizeUrl = `${nauthUrl}/authorize?${query}`;
  return authorizeUrl;
};

/**
 * request all active contracts from config api if missing
 *
 * @returns {Function} thunk to get active contracts
 */
const getActiveContractsThunk = () => async (dispatch, getState, { api }) => {
  // skip request if contract map already exists
  if (!isEmpty(contractMapSelector(getState()))) {
    return;
  }

  try {
    dispatch(getConfigRequest());
    const config = await api.config.getContracts();
    dispatch(getConfigSuccess(config));
  } catch (error) {
    getConfigFailure(error);
  }
};

/**
 * request audience tags from misc api if missing
 *
 * @returns {Function} thunk to get audience tags
 */
const getAudienceTagsThunk = () => async (dispatch, getState, { api }) => {
  // skip request if contract map already exists
  if (!isEmpty(audienceTagsSelector(getState()))) {
    return;
  }

  try {
    dispatch(getAudienceTagsRequest());
    const audienceTags = await api.misc.getAudienceTags();
    dispatch(getAudienceTagsSuccess(audienceTags));
  } catch (error) {
    getAudienceTagsFailure(error);
  }
};

/**
 * get required data for any route of the main app (CanvasApp)
 *
 * @returns {Function} thunk to get active contracts
 */
const getCanvasAppCommonThunk = function getCanvasAppCommonThunk() {
  return dispatch => {
    const thunks = [getActiveContractsThunk, getUserDataThunk, getAudienceTagsThunk];
    return Promise.all(thunks.map(fn => dispatch(fn())));
  };
};

/**
 * thunk for app route
 * request user's preferred home folder
 * @param {Function} dispatch - redux dispatch function
 * @param {Function} getState - redux getState function
 */
export const appThunk = async (dispatch, getState) => {
  // get common app data
  await dispatch(getCanvasAppCommonThunk());

  const state = getState();
  const tree = treeSelector(state);
  const userHomeFolderId = preferredHomeFolderIdSelector(state);

  // request user's home folder if missing from state
  if (!tree[userHomeFolderId]) {
    dispatch(getFolderThunk(userHomeFolderId));
  }

  // change to user home
  dispatch(changeDir(userHomeFolderId));
};

/**
 * thunk for component route
 * request component and switch mode
 * @param {Function} dispatch - redux dispatch function
 * @param {Function} getState - redux getState function
 */
export const componentThunk = async (dispatch, getState) => {
  // get common app data
  await dispatch(getCanvasAppCommonThunk());

  const state = getState();
  const cwd = cwdSelector(state);
  const tree = treeSelector(state);
  const userHomeFolderId = preferredHomeFolderIdSelector(state);

  // default to user home when there's no active cwd
  if (!cwd) {
    dispatch(changeDir(userHomeFolderId));

    // request user's home folder if missing from state
    if (!tree[userHomeFolderId]) {
      dispatch(getFolderThunk(userHomeFolderId));
    }
  }

  // switch to component mode and request data
  dispatch(switchApplicationMode(COMPONENT_TEMPLATE));
  const { id } = payloadSelector(state);
  const query = querySelector(state);
  try {
    // search param defaults
    const { locale = EN, version = PUBLISHED } = query;
    dispatch(isFetchingComponent(true));
    dispatch(getExistingComponent(id, version));
    const component = await getComponent({ id, locale, version });
    dispatch(getExistingComponentSuccess(id, component, EDIT_STATUS_SAVED));

    // if component is epxeriment, request experiment variants associated to the experiment name
    if (isExperiment(component) && !isBlank(component.Payload.experimentName)) {
      dispatch(fetchExperiment(component.Payload.experimentName));
    }
    dispatch(isFetchingComponent(false));
  } catch (error) {
    dispatch(getExistingComponentFailure(id, error));
    dispatch(notifyFailure(error.message));
  }
};

const baseContract = `
const contract = {
    contractSchemaVersion: "1",
    type: "contract",
    name: "UserDefined",
    displayName: "User Defined",
    children: []
};

module.exports = contract;
`.trim();

/**
 * thunk for contract preview route
 * ensure value query param exists and create default payload based on
 * @param {Function} dispatch - redux dispatch function
 * @param {Function} getState - redux getState function
 */
export const contractThunk = (dispatch, getState) => {
  const state = getState();
  const { value: contractStr } = querySelector(state);
  if (!contractStr) {
    dispatch(visitContract(baseContract));
    return;
  }

  try {
    const canvasContract = contractUtils.evalContractString(contractStr);
    const component = createNewComponentObject({ canvasContract });
    dispatch(getExistingComponentSuccess(component.Id, component));
  } catch {
    dispatch(visitContract(baseContract));
  }
};

/**
 * thunk for folder route
 * request folder
 * @param {Function} dispatch - redux dispatch function
 * @param {Function} getState - redux getState function
 */
export const folderThunk = async (dispatch, getState) => {
  // get common app data
  await dispatch(getCanvasAppCommonThunk());

  const state = getState();
  const { id } = payloadSelector(state);
  const userHomeFolderId = preferredHomeFolderIdSelector(state);

  // redirect to app for home folder
  if (id === userHomeFolderId) {
    dispatch(redirect(visitApp()));
    return;
  }

  // request folder data
  dispatch(getFolderThunk(id));
};

/**
 * thunk for reports route
 * request user
 * @param {Function} dispatch - redux dispatch function
 */
export const reportThunk = async dispatch => {
  // get common app data
  dispatch(getCanvasAppCommonThunk());
};

/**
 * thunk for login route
 * authenticate with nauth
 * @param {Function} dispatch - redux dispatch function
 * @param {Function} getState - redux getState function
 * @param {object} bag - redux first router bag
 * @param {object} bag.action - redux action describing route
 * @param {object} bag.extra - extra objects
 * @returns {null} nothing
 */
export const loginThunk = async (dispatch, getState, { extra }) => {
  const { location, nauth } = extra;

  // redux redirect to app if cookie already exists
  // only relevant when user visits root or login directly
  if (nauth.getNauthTokenCookie()) {
    return dispatch(redirect(visitApp()));
  }

  // location redirect for nauth authentication
  const redirectUri = `${location.origin}${PATHNAME_NAUTH}`;
  const authorizeUrl = buildAuthorizeUrl(redirectUri);
  return location.replace(authorizeUrl);
};

/**
 * thunk for logout route
 * revoke nauth token and remove cookie
 * @param {Function} dispatch - redux dispatch function
 * @param {Function} getState - redux getState function
 * @param {object} bag - redux first router bag
 * @param {object} bag.extra - extra objects
 */
export const logoutThunk = async (dispatch, getState, { extra }) => {
  const { nauth } = extra;

  try {
    await nauth.logout();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`'${err.body}' when logging out`);
  }

  nauth.removeNauthTokenCookie();
  nauth.removeNauthIdTokenCookie();
};

/**
 * thunk for nauth route
 * process data returned by nauth and save cookie
 * @param {Function} dispatch - redux dispatch function
 * @param {Function} getState - redux getState function
 * @param {object} bag - bag
 * @param {object} bag.action - redux action describing route
 * @param {object} bag.extra - extra objects
 * @returns {null} nothing
 */
export const nauthThunk = async (dispatch, getState, { action, extra }) => {
  const { location, nauth } = extra;

  // redux redirect to app if cookie already exists
  // only relevant when user visits nauth directly
  if (nauth.getNauthTokenCookie()) {
    return dispatch(redirect(visitApp()));
  }
  try {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const redirectUri = `${location.origin}${PATHNAME_NAUTH}`;
    const nauthResponse = await nauth.login({
      redirectUri,
      state: nauthState,
      code
    });
    const accessToken = nauthResponse.access_token;
    const idToken = nauthResponse.id_token;
    const expiresIn = nauthResponse.expires_in;

    // save token as a cookie that expires a minute before it actually does
    const age = Number(expiresIn) || 12 * 3600;
    nauth.setNauthTokenCookie(accessToken, age - 60);
    nauth.setNauthIdTokenCookie(idToken, age - 60);

    // redux redirect action object
    return dispatch(redirect(action));
  } catch (err) {
    // only happens when authenticated but decode/parse fails so go to app
    return dispatch(redirect(visitApp()));
  }
};

/**
 * thunk for page route
 * request home folder and page
 * @param {Function} dispatch - redux dispatch function
 * @param {Function} getState - redux getState function
 */
export const pageThunk = async (dispatch, getState) => {
  // get common app data
  await dispatch(getCanvasAppCommonThunk());

  const state = getState();
  const { id } = payloadSelector(state);
  const { locale = EN, version = PUBLISHED } = querySelector(state);

  // request page data
  dispatch(getPage({ id, locale, version }));
};

/**
 * thunk for search route
 * populate filters and switch mode
 * @param {Function} dispatch - redux dispatch function
 * @param {Function} getState - redux getState function
 */
export const searchThunk = async (dispatch, getState) => {
  // get common app data
  await dispatch(getCanvasAppCommonThunk());

  const state = getState();
  const cwd = cwdSelector(state);
  const tree = treeSelector(state);
  const userHomeFolderId = preferredHomeFolderIdSelector(state);

  // default to user home when there's no active cwd
  if (!cwd) {
    dispatch(changeDir(userHomeFolderId));

    // request user's home folder if missing from state
    if (!tree[userHomeFolderId]) {
      dispatch(getFolderThunk(userHomeFolderId));
    }
  }

  // switch to search mode, add filters, and send request
  dispatch(switchApplicationMode(SEARCH_TEMPLATE));
  const { context = 'component', filters: jsonFilters } = querySelector(state);
  try {
    const filters = JSON.parse(jsonFilters);
    dispatch(setFilters(context, filters));
    dispatch(getSearchResults(context, filters));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(err);
    dispatch(redirect(visitApp()));
  }
};
